﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useForm, useWatch, type Control } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  useAddTicketTypeMutation,
  useDeleteTicketTypeMutation,
  useUpdateTicketTypeMutation,
} from "./api";
import { isoToLocalInput, localInputToIso } from "./date-helpers";
import { useMeQuery } from "../organizers/api";
import { useConfirm } from "@/lib/confirm";
import { useFormat, type Format } from "@/lib/format";
import { useT } from "@/lib/i18n";

import {
  SUPPORTED_CURRENCIES,
  calculatePriceBreakdown,
  type CurrencyCode,
  type TicketType,
} from "@ticketnjoy/types";

// Boş datetime input değeri → null olarak normalize et.
const emptyToUndefined = z.preprocess(
  (v) => (v === "" || v == null ? undefined : v),
  z.string().optional(),
);

const schema = z
  .object({
    name: z.string().min(1, "validation.nameRequired").max(100),
    description: z.string().max(1000).optional().or(z.literal("")),
    price: z.coerce.number().min(0, "validation.priceMin"),
    currency: z.enum(SUPPORTED_CURRENCIES),
    quantity: z.coerce.number().int().min(1, "validation.capacityMin"),
    salesStart: emptyToUndefined,
    salesEnd: emptyToUndefined,
    minPerOrder: z.coerce.number().int().min(1, "validation.minPerOrder").max(100),
    // "" → undefined (limitsiz)
    maxPerOrder: z.preprocess(
      (v) => (v === "" || v == null ? undefined : v),
      z.coerce.number().int().min(1).max(100).optional(),
    ),
    // "" → undefined (event default'unu kullan)
    taxRate: z.preprocess(
      (v) => (v === "" || v == null ? undefined : v),
      z.coerce.number().min(0).max(100).optional(),
    ),
    sortOrder: z.coerce.number().int().default(0),
  })
  .refine(
    (v) =>
      v.salesStart == null ||
      v.salesEnd == null ||
      new Date(v.salesStart) < new Date(v.salesEnd),
    { message: "validation.salesWindow", path: ["salesEnd"] },
  )
  .refine(
    (v) => v.maxPerOrder == null || v.maxPerOrder >= v.minPerOrder,
    { message: "validation.maxBelowMin", path: ["maxPerOrder"] },
  );

type FormValues = z.infer<typeof schema>;
type FormInput = z.input<typeof schema>;

const CURRENCY_LABELS: Record<CurrencyCode, string> = {
  TRY: "₺ TRY",
  EUR: "€ EUR",
  USD: "$ USD",
};

/**
 * z.coerce ile form input tipi `unknown` oluyor — HTML Input'a güvenle pass
 * etmek için string/number'a normalize et. null/undefined → "" (controlled boş).
 */
function inputVal(v: unknown): string | number {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return v;
  return String(v);
}

/**
 * Event detay sayfasında kullanılan bilet türü listesi + inline ekleme/düzenleme.
 * Satış başlamışsa Quantity'i Sold altına düşüremez (backend kuralını UI'da da belli et).
 */
export function TicketTypesManager({
  eventSlug,
  ticketTypes,
  eventDefaultTaxRate,
  disabled,
}: {
  eventSlug: string;
  ticketTypes: TicketType[];
  eventDefaultTaxRate: number;
  disabled?: boolean;
}) {
  const t = useT("ticketTypes");
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: me } = useMeQuery();

  const orgServiceFee = me?.serviceFee ?? 0;
  const orgCommission = me?.commissionRate ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t("heading")}</h2>
        {!showAdd && !disabled && (
          <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}>
            <Plus className="me-1 h-4 w-4" /> {t("add")}
          </Button>
        )}
      </div>

      {ticketTypes.length === 0 && !showAdd && (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t("empty")}
        </p>
      )}

      <div className="space-y-2">
        {ticketTypes.map((ticket) =>
          editingId === ticket.id ? (
            <TicketTypeEditRow
              key={ticket.id}
              eventSlug={eventSlug}
              ticket={ticket}
              eventDefaultTaxRate={eventDefaultTaxRate}
              orgServiceFee={orgServiceFee}
              orgCommission={orgCommission}
              onDone={() => setEditingId(null)}
            />
          ) : (
            <TicketTypeRow
              key={ticket.id}
              eventSlug={eventSlug}
              ticket={ticket}
              eventDefaultTaxRate={eventDefaultTaxRate}
              orgServiceFee={orgServiceFee}
              orgCommission={orgCommission}
              disabled={disabled}
              onEdit={() => setEditingId(ticket.id)}
            />
          ),
        )}
      </div>

      {showAdd && (
        <TicketTypeAddRow
          eventSlug={eventSlug}
          eventDefaultTaxRate={eventDefaultTaxRate}
          orgServiceFee={orgServiceFee}
          orgCommission={orgCommission}
          onDone={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}

function TicketTypeRow({
  eventSlug,
  ticket,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
  disabled,
  onEdit,
}: {
  eventSlug: string;
  ticket: TicketType;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
  disabled?: boolean;
  onEdit: () => void;
}) {
  const deleteMutation = useDeleteTicketTypeMutation(eventSlug);
  const confirm = useConfirm();
  const tConfirm = useT("confirm.deleteTicketType");
  const t = useT("ticketTypes");
  const tCommon = useT("common");
  const fmt = useFormat();

  const taxRate = ticket.taxRate ?? eventDefaultTaxRate;
  const breakdown = calculatePriceBreakdown(
    ticket.price,
    taxRate,
    orgServiceFee,
    orgCommission,
  );

  const handleDelete = async () => {
    if (ticket.sold > 0) {
      toast.error(t("deleteBlocked"));
      return;
    }
    const ok = await confirm({
      title: tConfirm("title"),
      description: tConfirm("description", { name: ticket.name }),
      confirmLabel: tConfirm("action"),
      destructive: true,
    });
    if (!ok) return;
    deleteMutation.mutate(ticket.id, {
      onSuccess: () => toast.success(t("deleted")),
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <div className="rounded-md border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{ticket.name}</span>
            <Badge variant="secondary" className="font-mono">
              {ticket.currency}
            </Badge>
            {ticket.sold > 0 && (
              <Badge variant="outline">{t("sold", { count: ticket.sold })}</Badge>
            )}
            {ticket.taxRate != null && (
              <Badge variant="outline">
                {t("taxBadge", { value: fmt.percent(ticket.taxRate, 2) })}
              </Badge>
            )}
          </div>
          {ticket.description && (
            <p className="break-words text-xs text-muted-foreground">
              {ticket.description}
            </p>
          )}
          <p className="break-words text-sm text-muted-foreground">
            {t("base", { value: fmt.money(ticket.price, ticket.currency) })} ·{" "}
            {t("buyerPays")}{" "}
            <span className="font-semibold text-foreground">
              {fmt.money(breakdown.unitGross, ticket.currency)}
            </span>{" "}
            ·{" "}
            {t("available", {
              available: fmt.number(ticket.available),
              quantity: fmt.number(ticket.quantity),
            })}
          </p>
          {(ticket.salesStart || ticket.salesEnd) && (
            <p className="break-words text-xs text-muted-foreground">
              {t("salesWindow", { window: formatWindow(ticket.salesStart, ticket.salesEnd, fmt, t) })}
            </p>
          )}
        </div>
        {!disabled && (
          <div className="flex shrink-0 gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label={tCommon("edit")}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              aria-label={tCommon("delete")}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatWindow(
  start: string | null,
  end: string | null,
  fmt: Format,
  t: ReturnType<typeof useT>,
): string {
  const short = (iso: string) =>
    fmt.dateTime(iso, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  if (start && end) return t("window.between", { start: short(start), end: short(end) });
  if (start) return t("window.from", { start: short(start) });
  if (end) return t("window.until", { end: short(end) });
  return "";
}

function TicketTypeAddRow({
  eventSlug,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
  onDone,
}: {
  eventSlug: string;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
  onDone: () => void;
}) {
  const mutation = useAddTicketTypeMutation(eventSlug);
  const t = useT("ticketTypes");
  const tCommon = useT("common");
  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      currency: "TRY",
      quantity: 100,
      salesStart: "",
      salesEnd: "",
      minPerOrder: 1,
      maxPerOrder: "",
      taxRate: "",
      sortOrder: 0,
    },
  });

  const onSubmit = (values: FormValues) => {
    const payload = {
      name: values.name,
      description: values.description || null,
      price: values.price,
      currency: values.currency,
      quantity: values.quantity,
      salesStart: localInputToIso(values.salesStart ?? null),
      salesEnd: localInputToIso(values.salesEnd ?? null),
      minPerOrder: values.minPerOrder,
      maxPerOrder: values.maxPerOrder ?? null,
      taxRate: values.taxRate ?? null,
      sortOrder: values.sortOrder,
    };
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(t("added"));
        form.reset();
        onDone();
      },
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <InlineTicketForm
      form={form}
      title={t("add")}
      submitLabel={tCommon("add")}
      isSubmitting={mutation.isPending}
      eventDefaultTaxRate={eventDefaultTaxRate}
      orgServiceFee={orgServiceFee}
      orgCommission={orgCommission}
      onSubmit={onSubmit}
      onCancel={onDone}
    />
  );
}

function TicketTypeEditRow({
  eventSlug,
  ticket,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
  onDone,
}: {
  eventSlug: string;
  ticket: TicketType;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
  onDone: () => void;
}) {
  const mutation = useUpdateTicketTypeMutation(eventSlug, ticket.id);
  const t = useT("ticketTypes");
  const tCommon = useT("common");
  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: ticket.name,
      description: ticket.description ?? "",
      price: ticket.price,
      currency: ticket.currency,
      quantity: ticket.quantity,
      salesStart: isoToLocalInput(ticket.salesStart),
      salesEnd: isoToLocalInput(ticket.salesEnd),
      minPerOrder: ticket.minPerOrder,
      maxPerOrder: ticket.maxPerOrder ?? "",
      taxRate: ticket.taxRate ?? "",
      sortOrder: ticket.sortOrder,
    },
  });

  const onSubmit = (values: FormValues) => {
    if (values.quantity < ticket.sold) {
      toast.error(t("capacityBelowSold", { sold: ticket.sold }));
      return;
    }
    const payload = {
      name: values.name,
      description: values.description || null,
      price: values.price,
      currency: values.currency,
      quantity: values.quantity,
      salesStart: localInputToIso(values.salesStart ?? null),
      salesEnd: localInputToIso(values.salesEnd ?? null),
      minPerOrder: values.minPerOrder,
      maxPerOrder: values.maxPerOrder ?? null,
      taxRate: values.taxRate ?? null,
      sortOrder: values.sortOrder,
    };
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(t("updated"));
        onDone();
      },
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <InlineTicketForm
      form={form}
      title={t("editTitle", { name: ticket.name })}
      submitLabel={tCommon("save")}
      isSubmitting={mutation.isPending}
      eventDefaultTaxRate={eventDefaultTaxRate}
      orgServiceFee={orgServiceFee}
      orgCommission={orgCommission}
      onSubmit={onSubmit}
      onCancel={onDone}
    />
  );
}

function InlineTicketForm({
  form,
  title,
  submitLabel,
  isSubmitting,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
  onSubmit,
  onCancel,
}: {
  // RHF + zod `coerce/preprocess` tip köprüsünü basitleştirmek için pragmatik
  // olarak FormInput ile FormValues'u aynı hizaya alıyoruz. Form validasyonu
  // yine `zodResolver(schema)` üzerinden sağlanıyor, handleSubmit FormValues döndürüyor.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: ReturnType<typeof useForm<any, unknown, FormValues>>;
  title: string;
  submitLabel: string;
  isSubmitting: boolean;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
}) {
  const t = useT("ticketTypes");
  const tCommon = useT("common");
  const fmt = useFormat();
  return (
    <div className="rounded-md border-2 border-dashed p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          type="button"
          aria-label={tCommon("close")}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/*
           * 3 anlamlı bölüm:
           *   1) Bilet Bilgileri — kimlik
           *   2) Fiyat & Stok — para/adet/KDV
           *   3) Satış Ayarları — tarih penceresi + sepet limitleri
           *
           * Her bölüm aynı 3-kolonlu grid üzerinde hizalanır (kolon sınırları
           * bölümler arası tutarlı kalsın). Mobilde tek kolon, sm+ üç kolon.
           * `items-start` olmazsa textarea gibi uzun alanlar komşu label'ları
           * aşağı kaydırır.
           */}

          <Section title={t("sections.info")}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>{t("name")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("namePlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("sortOrder")}</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} value={inputVal(field.value)} />
                  </FormControl>
                  <FormDescription>{t("sortOrderHint")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="sm:col-span-3">
                  <FormLabel>{t("description")}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <Section title={t("sections.price")}>
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("price")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>{t("priceHint")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("currency")}</FormLabel>
                  <Select
                    value={(field.value as CurrencyCode | undefined) ?? "TRY"}
                    onValueChange={(v) => field.onChange(v as CurrencyCode)}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SUPPORTED_CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {CURRENCY_LABELS[c]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("quantity")}</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} value={inputVal(field.value)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="taxRate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("tax")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      placeholder={t("taxPlaceholder", { value: fmt.percent(eventDefaultTaxRate, 2) })}
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>{t("taxHint")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <Section title={t("sections.sales")}>
            <FormField
              control={form.control}
              name="salesStart"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("salesStart")}</FormLabel>
                  <FormControl>
                    <Input
                      type="datetime-local"
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>{t("salesStartHint")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="salesEnd"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("salesEnd")}</FormLabel>
                  <FormControl>
                    <Input
                      type="datetime-local"
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>{t("salesEndHint")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* 3. hücre bilinçli boş: salesEnd ile aynı satırda min/max'in
                kaymaması için kolon ızgarasını koruyoruz. */}
            <div aria-hidden className="hidden sm:block" />
            <FormField
              control={form.control}
              name="minPerOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("minPerOrder")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>{t("minHint")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maxPerOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("maxPerOrder")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      placeholder={t("maxPlaceholder")}
                      {...field}
                      value={inputVal(field.value)}
                    />
                  </FormControl>
                  <FormDescription>{t("maxHint")}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <PricePreview
            control={form.control}
            eventDefaultTaxRate={eventDefaultTaxRate}
            orgServiceFee={orgServiceFee}
            orgCommission={orgCommission}
          />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel}>
              {tCommon("cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {submitLabel}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

/**
 * Canlı fiyat ön-izleme. Organizer panelinde şeffaflık için hem alıcının
 * ödeyeceği brüt hem de platform kesildikten sonra organizer'a kalan net gösterilir.
 */
function PricePreview({
  control,
  eventDefaultTaxRate,
  orgServiceFee,
  orgCommission,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  eventDefaultTaxRate: number;
  orgServiceFee: number;
  orgCommission: number;
}) {
  const values = useWatch({ control }) as Partial<FormInput>;
  const price = Number(values.price) || 0;
  const currency = (values.currency ?? "TRY") as CurrencyCode;
  const taxInput = values.taxRate;
  const effectiveTax =
    taxInput === "" || taxInput == null ? eventDefaultTaxRate : Number(taxInput);

  const breakdown = calculatePriceBreakdown(
    price,
    effectiveTax,
    orgServiceFee,
    orgCommission,
  );

  const organizerNet = price - breakdown.commission;
  const t = useT("ticketTypes.preview");
  const fmt = useFormat();

  return (
    <div className="rounded-md border bg-muted/30 p-3 text-xs">
      <p className="mb-2 font-semibold text-foreground">{t("title")}</p>
      <div className="grid gap-1 sm:grid-cols-2">
        <Row label={t("base")} value={fmt.money(price, currency)} />
        <Row
          label={t("tax", { value: fmt.percent(effectiveTax, 2) })}
          value={fmt.money(breakdown.taxAmount, currency)}
        />
        <Row label={t("serviceFee")} value={fmt.money(breakdown.serviceFee, currency)} />
        <Row
          label={t("commission", { value: fmt.percent(orgCommission, 2) })}
          value={`− ${fmt.money(breakdown.commission, currency)}`}
        />
      </div>
      <div className="mt-2 flex justify-between border-t pt-2 font-semibold text-foreground">
        <span>{t("buyerPays")}</span>
        <span>{fmt.money(breakdown.unitGross, currency)}</span>
      </div>
      <div className="mt-1 flex justify-between text-muted-foreground">
        <span>{t("youGet")}</span>
        <span>{fmt.money(Math.max(0, organizerNet), currency)}</span>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums text-foreground">{value}</span>
    </div>
  );
}

/**
 * Form'u anlamlı bölümlere ayıran mini kart. Event detay formundaki
 * `VenueFields` / `DateFields` pattern'iyle birebir tutarlı:
 *   `rounded-lg border p-4` + küçük başlık.
 *
 * 3-kolonlu grid bölüm içinde geçerli; mobilde tek kolona düşer.
 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 rounded-lg border p-4">
      <h4 className="text-sm font-semibold">{title}</h4>
      <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-3">
        {children}
      </div>
    </section>
  );
}
